/**
 * mSTEM Device Connection Modal (GEN-03 S-4..S-9 / GEN-04 S-3..S-8). MOCK-ONLY.
 *
 * ONE reusable presentational component parameterized by `deviceType`
 * ('robot' | 'kit') so the Robot Manager and Kit Manager share the same
 * 4-transport mock connection wizard without duplication.
 *
 * Transports (UC-03/UC-04): 4G / Wi-Fi / Bluetooth / Dây nối (USB).
 *
 * Flow (all driven by the container's local state, mirroring the firmware
 * wizard pattern — NO real getUserMedia/BLE/serial/network):
 *   phase 'pick'    — transport picker (4 cards)
 *   phase 'form'    — short per-transport mock form
 *                       Wi-Fi    → SSID + mật khẩu
 *                       4G       → thông tin SIM (auto)
 *                       Bluetooth→ danh sách thiết bị quét được giả + chọn
 *                       Dây nối  → chọn cổng giả
 *   phase 'connecting' — "Đang kết nối…" mock ProgressBar (container timer)
 *   phase 'success' — "Kết nối [tên] thành công" success box + transient toast
 *
 * White body enforced in CSS (CONTRAST FIX) so the navy overlay never shows.
 * All strings via react-intl (gen.conn.* ids). Class component + bindAll so
 * onChange handlers satisfy react/jsx-no-bind without inline arrow allocations.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import ProgressBar from '@ramonak/react-progress-bar';
import {FormattedMessage, defineMessages, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import styles from './device-connection-modal.css';

const messages = defineMessages({
    titleRobot: {
        defaultMessage: 'Kết nối Robot',
        description: 'Connection modal title — robot',
        id: 'gen.conn.title.robot'
    },
    titleKit: {
        defaultMessage: 'Kết nối kit SmartHome',
        description: 'Connection modal title — kit',
        id: 'gen.conn.title.kit'
    },
    pickPrompt: {
        defaultMessage: 'Chọn phương thức kết nối:',
        description: 'Prompt above the transport picker',
        id: 'gen.conn.pick.prompt'
    },
    transport4g: {
        defaultMessage: '4G',
        description: 'Transport option — 4G',
        id: 'gen.conn.transport.4g'
    },
    transport4gDesc: {
        defaultMessage: 'Kết nối qua mạng di động 4G',
        description: 'Transport description — 4G',
        id: 'gen.conn.transport.4g.desc'
    },
    transportWifi: {
        defaultMessage: 'Wi-Fi',
        description: 'Transport option — Wi-Fi',
        id: 'gen.conn.transport.wifi'
    },
    transportWifiDesc: {
        defaultMessage: 'Kết nối qua mạng Wi-Fi',
        description: 'Transport description — Wi-Fi',
        id: 'gen.conn.transport.wifi.desc'
    },
    transportBt: {
        defaultMessage: 'Bluetooth',
        description: 'Transport option — Bluetooth',
        id: 'gen.conn.transport.bt'
    },
    transportBtDesc: {
        defaultMessage: 'Quét và kết nối qua Bluetooth',
        description: 'Transport description — Bluetooth',
        id: 'gen.conn.transport.bt.desc'
    },
    transportUsb: {
        defaultMessage: 'Dây nối (USB)',
        description: 'Transport option — USB cable',
        id: 'gen.conn.transport.usb'
    },
    transportUsbDesc: {
        defaultMessage: 'Kết nối qua dây cáp USB',
        description: 'Transport description — USB',
        id: 'gen.conn.transport.usb.desc'
    },
    wifiSsid: {
        defaultMessage: 'Tên mạng (SSID)',
        description: 'Wi-Fi SSID field label',
        id: 'gen.conn.wifi.ssid'
    },
    wifiPassword: {
        defaultMessage: 'Mật khẩu Wi-Fi',
        description: 'Wi-Fi password field label',
        id: 'gen.conn.wifi.password'
    },
    simInfo: {
        defaultMessage: 'Thông tin SIM',
        description: '4G SIM info field label',
        id: 'gen.conn.4g.sim'
    },
    simAuto: {
        defaultMessage: 'Tự động phát hiện SIM (MBF-4G)',
        description: '4G auto-detected SIM placeholder value',
        id: 'gen.conn.4g.auto'
    },
    btScanning: {
        defaultMessage: 'Thiết bị Bluetooth quét được:',
        description: 'Bluetooth scanned-devices list label',
        id: 'gen.conn.bt.scanning'
    },
    usbPort: {
        defaultMessage: 'Chọn cổng kết nối',
        description: 'USB port select label',
        id: 'gen.conn.usb.port'
    },
    connecting: {
        defaultMessage: 'Đang kết nối…',
        description: 'Connecting progress label',
        id: 'gen.conn.connecting'
    },
    successRobot: {
        defaultMessage: 'Kết nối {name} thành công',
        description: 'Robot connection success message',
        id: 'gen.conn.success.robot'
    },
    successKit: {
        defaultMessage: 'Kết nối {name} thành công',
        description: 'Kit connection success message',
        id: 'gen.conn.success.kit'
    },
    successHintRobot: {
        defaultMessage: 'Kết nối robot mới thành công. Bạn có thể bắt đầu lập trình.',
        description: 'Robot post-connect hint (UC-03 S-9)',
        id: 'gen.conn.success.hint.robot'
    },
    successHintKit: {
        defaultMessage: 'Kết nối kit SmartHome mới thành công. Bạn có thể bắt đầu lập trình.',
        description: 'Kit post-connect hint (UC-04 S-8)',
        id: 'gen.conn.success.hint.kit'
    },
    back: {
        defaultMessage: 'Quay lại',
        description: 'Back button',
        id: 'gen.conn.btn.back'
    },
    cancel: {
        defaultMessage: 'Huỷ',
        description: 'Cancel button',
        id: 'gen.conn.btn.cancel'
    },
    connect: {
        defaultMessage: 'Kết nối',
        description: 'Start-connect button',
        id: 'gen.conn.btn.connect'
    },
    close: {
        defaultMessage: 'Đóng',
        description: 'Close button on success',
        id: 'gen.conn.btn.close'
    }
});

const TRANSPORTS = [
    {key: '4g', icon: '📶', label: 'transport4g', desc: 'transport4gDesc'},
    {key: 'wifi', icon: '📡', label: 'transportWifi', desc: 'transportWifiDesc'},
    {key: 'bluetooth', icon: '🔵', label: 'transportBt', desc: 'transportBtDesc'},
    {key: 'usb', icon: '🔌', label: 'transportUsb', desc: 'transportUsbDesc'}
];

// Mock data — no real scanning. Static fake device + port lists.
const MOCK_BT_DEVICES = ['MBF-BT-01', 'MBF-BT-02', 'SmartHome-Hub-A3'];
const MOCK_USB_PORTS = ['COM3', 'COM4', '/dev/ttyUSB0'];

class DeviceConnectionModalComponent extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleSsidChange',
            'handlePasswordChange',
            'handleBtSelect',
            'handlePortChange'
        ]);
    }
    handleSsidChange (e) {
        this.props.onFormChange('ssid', e.target.value);
    }
    handlePasswordChange (e) {
        this.props.onFormChange('password', e.target.value);
    }
    handleBtSelect (e) {
        this.props.onFormChange('btDevice', e.target.value);
    }
    handlePortChange (e) {
        this.props.onFormChange('usbPort', e.target.value);
    }
    canConnect () {
        const {transport, form} = this.props;
        if (transport === 'wifi') return Boolean(form.ssid);
        if (transport === 'bluetooth') return Boolean(form.btDevice);
        if (transport === 'usb') return Boolean(form.usbPort);
        return true; // 4G auto
    }
    renderForm () {
        const {intl, transport, form} = this.props;
        if (transport === 'wifi') {
            return (
                <div className={styles.formGrid}>
                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>
                            <FormattedMessage {...messages.wifiSsid} />
                        </span>
                        <input
                            className={styles.input}
                            type="text"
                            value={form.ssid || ''}
                            onChange={this.handleSsidChange}
                        />
                    </label>
                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>
                            <FormattedMessage {...messages.wifiPassword} />
                        </span>
                        <input
                            className={styles.input}
                            type="password"
                            value={form.password || ''}
                            onChange={this.handlePasswordChange}
                        />
                    </label>
                </div>
            );
        }
        if (transport === '4g') {
            return (
                <div className={styles.formGrid}>
                    <label className={styles.field}>
                        <span className={styles.fieldLabel}>
                            <FormattedMessage {...messages.simInfo} />
                        </span>
                        <input
                            disabled
                            className={styles.input}
                            type="text"
                            value={intl.formatMessage(messages.simAuto)}
                        />
                    </label>
                </div>
            );
        }
        if (transport === 'bluetooth') {
            return (
                <div className={styles.formGrid}>
                    <span className={styles.fieldLabel}>
                        <FormattedMessage {...messages.btScanning} />
                    </span>
                    <select
                        className={styles.input}
                        size={3}
                        value={form.btDevice || ''}
                        onChange={this.handleBtSelect}
                    >
                        {MOCK_BT_DEVICES.map(d => (
                            <option
                                key={d}
                                value={d}
                            >
                                {d}
                            </option>
                        ))}
                    </select>
                </div>
            );
        }
        // usb
        return (
            <div className={styles.formGrid}>
                <label className={styles.field}>
                    <span className={styles.fieldLabel}>
                        <FormattedMessage {...messages.usbPort} />
                    </span>
                    <select
                        className={styles.input}
                        value={form.usbPort || ''}
                        onChange={this.handlePortChange}
                    >
                        <option value="">{'—'}</option>
                        {MOCK_USB_PORTS.map(p => (
                            <option
                                key={p}
                                value={p}
                            >
                                {p}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
        );
    }
    render () {
        const {
            deviceName,
            deviceType,
            intl,
            phase,
            progress,
            onCancel,
            onPickTransport,
            onBack,
            onStartConnect
        } = this.props;
        const isKit = deviceType === 'kit';
        const title = isKit ?
            intl.formatMessage(messages.titleKit) :
            intl.formatMessage(messages.titleRobot);
        const successMsg = isKit ? messages.successKit : messages.successRobot;
        const successHint = isKit ? messages.successHintKit : messages.successHintRobot;

        return (
            <Modal
                className={styles.modalContent}
                contentLabel={title}
                onRequestClose={onCancel}
            >
                <div className={styles.body}>
                    {phase === 'pick' ? (
                        <div>
                            <p className={styles.prompt}>
                                <FormattedMessage {...messages.pickPrompt} />
                            </p>
                            <div className={styles.transportGrid}>
                                {TRANSPORTS.map(t => (
                                    <button
                                        key={t.key}
                                        className={styles.transportCard}
                                        type="button"
                                        data-transport={t.key}
                                        onClick={() => onPickTransport(t.key)} // eslint-disable-line react/jsx-no-bind
                                    >
                                        <span
                                            className={styles.transportIcon}
                                            aria-hidden="true"
                                        >
                                            {t.icon}
                                        </span>
                                        <span className={styles.transportLabel}>
                                            {intl.formatMessage(messages[t.label])}
                                        </span>
                                        <span className={styles.transportDesc}>
                                            {intl.formatMessage(messages[t.desc])}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {phase === 'form' ? (
                        <div>
                            {this.renderForm()}
                        </div>
                    ) : null}

                    {phase === 'connecting' ? (
                        <div className={styles.progressWrap}>
                            <p className={styles.prompt}>
                                <FormattedMessage {...messages.connecting} />
                            </p>
                            <div className={styles.progressMeta}>
                                <span className={styles.progressPercent}>
                                    {`${Math.round(progress)}%`}
                                </span>
                            </div>
                            <ProgressBar
                                baseBgColor="#D9E3F2"
                                bgColor="#162A75"
                                completed={Math.round(progress)}
                                height="8px"
                                isLabelVisible={false}
                            />
                        </div>
                    ) : null}

                    {phase === 'success' ? (
                        <div>
                            <div className={styles.successBox}>
                                <span
                                    className={styles.successCheck}
                                    aria-hidden="true"
                                >
                                    {'✓'}
                                </span>
                                <FormattedMessage
                                    {...successMsg}
                                    values={{name: deviceName}}
                                />
                            </div>
                            <p className={styles.successHint}>
                                <FormattedMessage {...successHint} />
                            </p>
                            <div className={styles.toast}>
                                <FormattedMessage
                                    {...successMsg}
                                    values={{name: deviceName}}
                                />
                            </div>
                        </div>
                    ) : null}

                    <div className={styles.footer}>
                        {phase === 'form' ? (
                            <button
                                className={styles.btnSecondary}
                                type="button"
                                onClick={onBack}
                            >
                                <FormattedMessage {...messages.back} />
                            </button>
                        ) : null}
                        {phase === 'pick' || phase === 'form' ? (
                            <button
                                className={styles.btnSecondary}
                                type="button"
                                onClick={onCancel}
                            >
                                <FormattedMessage {...messages.cancel} />
                            </button>
                        ) : null}
                        {phase === 'form' ? (
                            <button
                                className={styles.btnPrimary}
                                disabled={!this.canConnect()}
                                type="button"
                                onClick={onStartConnect}
                            >
                                <FormattedMessage {...messages.connect} />
                            </button>
                        ) : null}
                        {phase === 'success' ? (
                            <button
                                className={styles.btnPrimary}
                                type="button"
                                onClick={onCancel}
                            >
                                <FormattedMessage {...messages.close} />
                            </button>
                        ) : null}
                    </div>
                </div>
            </Modal>
        );
    }
}

DeviceConnectionModalComponent.propTypes = {
    deviceName: PropTypes.string,
    deviceType: PropTypes.oneOf(['robot', 'kit']).isRequired,
    form: PropTypes.shape({
        ssid: PropTypes.string,
        password: PropTypes.string,
        btDevice: PropTypes.string,
        usbPort: PropTypes.string
    }).isRequired,
    intl: intlShape.isRequired,
    onBack: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onFormChange: PropTypes.func.isRequired,
    onPickTransport: PropTypes.func.isRequired,
    onStartConnect: PropTypes.func.isRequired,
    phase: PropTypes.oneOf(['pick', 'form', 'connecting', 'success']).isRequired,
    progress: PropTypes.number.isRequired,
    transport: PropTypes.oneOf(['4g', 'wifi', 'bluetooth', 'usb'])
};

export default injectIntl(DeviceConnectionModalComponent);
